/**
 * Config-driven masters: one spec per master drives the list columns, the create/edit sheet, the
 * inline quick-add and the permission gates. Schemas come from @nexseed/shared/masters so the web
 * validates exactly what the API enforces.
 */
import { masterPermissions } from "@nexseed/shared";
import type { MasterKey, Permission, SyncModule } from "@nexseed/shared";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { z } from "zod";
import { text } from "@/lib/text";

// The key list lives in @nexseed/shared because the permission vocabulary derives each master's
// four codes from it, and the API's registerCrud reads the same four off its own spec.
export type { MasterKey } from "@nexseed/shared";

export type MasterRow = Record<string, unknown> & {
  id: number;
  isActive?: boolean;
  source?: string;
  coreDeletedAt?: string | null;
};

interface FieldBase {
  name: string;
  label: string;
  description?: string | undefined;
  placeholder?: string | undefined;
  required?: boolean | undefined;
  /** Sheet is a 2-column grid. */
  colSpan?: 1 | 2 | undefined;
  showIf?: ((values: Record<string, unknown>) => boolean) | undefined;
  /**
   * nexseed's own field on a Core-synced master (e.g. a crop's max moisture): the sync never writes
   * it, so it stays editable on Core rows while every Core-owned field on the sheet is locked.
   */
  localOnCore?: true | undefined;
}
export type FieldSpec =
  | (FieldBase & { type: "treatment-mappings" | "drying-trays" })
  | (FieldBase & {
      type: "text" | "textarea" | "phone" | "email" | "date" | "datetime";
      transform?: "upper" | undefined;
    })
  | (FieldBase & {
      type: "number";
      step?: number | undefined;
      min?: number | undefined;
    })
  | (FieldBase & { type: "checkbox" | "switch" })
  | (FieldBase & {
      type: "enum";
      options: { value: string; label: string }[];
      nullable?: boolean | undefined;
    })
  | (FieldBase & {
      type: "reference";
      master: MasterKey;
      /** Filter the lookup by another field's value, e.g. varieties by cropId. */
      dependsOn?: { field: string; param: string } | undefined;
      /** A fixed filter on the lookup, e.g. only pack sizes out of the Resource Master. */
      lookupFilter?: Record<string, string> | undefined;
      nullable?: boolean | undefined;
    });

export interface ColumnSpec {
  id: string;
  header: string;
  kind?:
    | "text"
    | "mono"
    | "bool"
    | "status"
    | "date"
    | "datetime"
    | "reference"
    | "number"
    | undefined;
  reference?: MasterKey | undefined;
  sortable?: boolean | undefined;
  align?: "left" | "right" | undefined;
  cell?: ((row: MasterRow) => ReactNode) | undefined;
}

/** A master loaded from a CSV sheet: the endpoint, the column guide and a template to download. */
export interface MasterImporter {
  path: string;
  /** What the rows become, for the dialog title and toast ("Stores vendors"). */
  noun?: string | undefined;
  help: string;
  /** The format, column by column, in the order of the template's header. */
  columns: { name: string; required?: boolean; note: string }[];
  /** Example rows under the header (the header itself is built from `columns`). */
  sample: string[][];
  /** Masters the import may add rows to, to refresh after it. */
  invalidates: MasterKey[];
}

export interface MasterSpec<S extends z.ZodTypeAny = z.ZodTypeAny> {
  key: MasterKey;
  title: string;
  singular: string;
  /** What vnr-prod called the same screen. */
  legacyName?: string | undefined;
  description: string;
  icon: LucideIcon;
  /**
   * The four codes this screen enforces. Per screen and per verb, so an admin can grant Vendors
   * without granting Varieties, and read without write. Filled in by `defineMaster` from `key` —
   * never written by hand, so a master cannot be wired to another master's codes.
   */
  permission: {
    view: Permission;
    add: Permission;
    edit: Permission;
    delete: Permission;
  };
  schema: S;
  columns: ColumnSpec[];
  fields: FieldSpec[];
  searchPlaceholder: string;
  defaultSort: string;
  /** No isActive column: Delete removes the row. */
  hardDelete?: boolean | undefined;
  /**
   * VNR Core owns this master: the list is a read-only mirror. No create, no edit, no delete, and
   * no quick-create back door — rows appear when the sync brings them and are disabled (never
   * deleted) when Core stops sending them, so pickers hide them but existing documents keep their
   * names. The page shows the sync state and a Pull from Core instead.
   */
  coreSynced?: SyncModule | undefined;
  /**
   * A Core-owned master that also takes hand-made rows (vendors: traders Core does not know). New
   * rows are saved as source=custom, which the sync never touches, so they stay fully editable;
   * the Core rows stay locked.
   */
  allowCustom?: boolean | undefined;
  /** A page of its own for New (vendors: the one-screen form with the address) instead of the sheet. */
  createPath?: "/masters/vendors/new" | undefined;
  /**
   * One extra list filter beside the search box, sent to the API as `param` (a `filterColumns` key
   * on the crud route). Kept in the URL as `filter`.
   */
  listFilter?:
    | {
        param: string;
        label: string;
        allLabel: string;
        /** One fixed view, no picker (a Stores-only login's Vendors are the Stores vendors). */
        locked?: boolean | undefined;
        options: {
          value: string;
          label: string;
          /** Sent instead of `param=value`, for an option that filters another column. */
          query?: Record<string, string> | undefined;
          /** This view has nothing to do with VNR Core: no sync strip or Pull from Core. */
          noCoreSync?: boolean | undefined;
          /** A CSV import for this view only (Stores vendors, in the whole Vendor master). */
          importer?: MasterImporter | undefined;
          /** New from this view opens with this search (Stores vendors: the locked Stores form). */
          createSearch?: { stores?: number } | undefined;
        }[];
      }
    | undefined;
  /** Loading the master from a CSV sheet (the Stores masters): the endpoint, its format. */
  importer?: MasterImporter | undefined;
  /** Open a detail page instead of the sheet on row click. */
  detailPath?: string | undefined;
  /** Label for options and reference cells. */
  optionLabel?: ((row: MasterRow) => string) | undefined;
  optionHint?: ((row: MasterRow) => string | undefined) | undefined;
  /** Extra query params on lookups (e.g. only active). */
  formSize?: "md" | "lg" | undefined;
  /** Rendered under the fields in the create/edit sheet, for what a field cannot hold (a plant's photos). */
  sheetSection?: ((row: MasterRow | undefined) => ReactNode) | undefined;
}

export const defineMaster = <S extends z.ZodTypeAny>(
  s: Omit<MasterSpec<S>, "permission">
): MasterSpec<S> => ({ ...s, permission: masterPermissions(s.key) });
export const masterPath = (key: MasterKey) => `/masters/${key}`;
export const labelOf = (spec: MasterSpec, row: MasterRow): string =>
  spec.optionLabel?.(row) ?? text(row.name ?? row.code ?? row.id);
